import { usePortalGuard } from '@glplus/glpweb-shared-libs';
import { useTranslation } from 'react-i18next';
import { Routes, Route, Navigate, useLocation } from 'react-router';
import { Box, Spinner, Alert, PanelCard, Typography } from '@glplus/glpweb-dsl';
import { CapabilitiesProvider, useVendor } from './lib/CapabilitiesContext';
import { SetupProvider, useSetup } from './lib/SetupContext';
import MainLayout from './components/MainLayout';
import SetupBanner from './components/SetupBanner';
import Dashboard from './routes/Dashboard';
import StoreProfile from './routes/StoreProfile';
import Team from './routes/Team';
import Help from './routes/Help';
import SetupPending from './routes/SetupPending';
import SetupWizard from './routes/SetupWizard';
import PendingStatus from './routes/PendingStatus';
import Login from './routes/Login';
import ComingSoon from './routes/ComingSoon';
import Promotions from './routes/Promotions';
import Circulars from './routes/Circulars';
import Reports from './routes/Reports';
import Orders from './routes/Orders';
import OrderDetail from './routes/OrderDetail';
import Payouts from './routes/Payouts';
import AdjustmentAppeal from './routes/AdjustmentAppeal';
import LocationsList from './routes/locations/LocationsList';
import LocationAdd from './routes/locations/LocationAdd';
import LocationImport from './routes/locations/LocationImport';
import LocationDetail from './routes/locations/LocationDetail';
import LocationArchives from './routes/locations/LocationArchives';
import ProductsList from './routes/products/ProductsList';
import ProductDetail from './routes/products/ProductDetail';
import AddProducts from './routes/products/AddProducts';
import AddProduct from './routes/products/AddProduct';
import ImportSpreadsheet from './routes/products/ImportSpreadsheet';
import ImportResults from './routes/products/ImportResults';
import { LocationScopeProvider } from './lib/LocationScopeContext';
import BusinessHome from './routes/business/BusinessHome';
import SupportContacts from './routes/business/SupportContacts';
import Certifications from './routes/business/Certifications';
import Ownership from './routes/business/Ownership';

const PORTAL_URLS = {
  consumer: import.meta.env.VITE_PORTAL_CONSUMER_URL as string,
  driver: import.meta.env.VITE_PORTAL_DRIVER_URL as string,
  vendor: import.meta.env.VITE_PORTAL_VENDOR_URL as string,
  admin: import.meta.env.VITE_PORTAL_ADMIN_URL as string,
};

// Pool-per-actor (vendor build Phase 0, 2026-09-17 §15gg N2): the portal owns
// its sign-in against the VENDOR pool. Same origin, so the guard's
// `?next=<href>` round-trips through /login → safeNextPath.
const LOGIN_URL = '/login';

const APPLY_URL = `${(import.meta.env.VITE_PUBLIC_SITE_URL as string | undefined) ?? 'https://www.grocerylistplus.com'}/vendors/apply`;

// Applications in a terminal state are done — the person may start a fresh
// request on the public site.
const TERMINAL_APPLICATION = new Set(['declined', 'withdrawn']);

export default function App() {
  const { pathname } = useLocation();
  // /login renders OUTSIDE the guard — the guard redirects here, so guarding
  // it would loop.
  if (pathname === '/login') {
    return <Login />;
  }
  return <GuardedApp />;
}

function GuardedApp() {
  const { t } = useTranslation();
  const guard = usePortalGuard({
    requiredPortal: 'vendor',
    portalUrls: PORTAL_URLS,
    loginUrl: LOGIN_URL,
    // The vendor pool has NO groups — pool membership (admin-create-only,
    // provisioned by an application or a team invite) is the perimeter, so
    // any valid session is admitted. What they can see/do is decided by data
    // (membership / application) and re-checked by vendor-api per request.
    admitAnyAuthenticated: true,
    // Local-iteration escape hatch (Runbook AM). NEVER set in a deployed build.
    disabled: import.meta.env.VITE_DISABLE_PORTAL_GUARD === 'true',
  });

  if (guard.isLoading) {
    return <Loading message={t('common.checkingAccess')} />;
  }
  if (guard.status !== 'authorized') {
    return <Loading message={t('common.redirecting')} />;
  }

  return (
    <CapabilitiesProvider>
      <PortalRouter />
    </CapabilitiesProvider>
  );
}

/**
 * Status-gated routing: a member sees the full portal; an applicant with an
 * in-flight request sees the pending screen; an account with neither (an
 * invited teammate whose membership was removed, or a declined applicant) is
 * told where to go.
 */
function PortalRouter() {
  const { t } = useTranslation();
  const { loading, error, data, activeVendorId } = useVendor();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <Spinner color="brand" />
      </Box>
    );
  }
  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const hasMembership = (data?.memberships.length ?? 0) > 0;
  if (hasMembership) {
    return (
      <SetupProvider>
        <LocationScopeProvider vendorId={activeVendorId}>
          <MemberApp />
        </LocationScopeProvider>
      </SetupProvider>
    );
  }

  const app = data?.application ?? null;
  if (app && !TERMINAL_APPLICATION.has(app.status)) {
    return (
      <MainLayout>
        <PendingStatus />
      </MainLayout>
    );
  }
  return (
    <MainLayout>
      <PanelCard accent="#F67A1A" title={t('noAccess.title')} sx={{ height: 'auto', maxWidth: 640 }}>
        <Typography sx={{ mb: 1 }}>{t('noAccess.body')}</Typography>
        <Typography>
          <Box component="a" href={APPLY_URL} sx={{ color: '#A85200', fontWeight: 700 }}>
            {t('noAccess.applyLink')}
          </Box>
        </Typography>
      </PanelCard>
    </MainLayout>
  );
}

/**
 * Member area. The setup wizard is a dedicated full-screen flow (its own
 * chrome); every other page renders inside the portal shell (header + rail +
 * footer) with the SetupBanner pinned on top until the required steps are done.
 */
function MemberApp() {
  const { pathname } = useLocation();

  if (pathname.startsWith('/setup')) {
    return <SetupWizard />;
  }

  return (
    <MainLayout>
      <SetupBanner />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/products" element={<ProductsList />} />
        <Route path="/products/add" element={<AddProducts />} />
        <Route path="/products/add/new" element={<AddProduct />} />
        <Route path="/products/add/import" element={<ImportSpreadsheet />} />
        <Route path="/products/add/import/:batchId" element={<ImportResults />} />
        <Route path="/products/:vendorProductId" element={<ProductDetail />} />
        {/* Phase 1 — Locations (§15z L0–L5). Import (L4a/L4b) lands with B1's column ruling. */}
        <Route path="/locations" element={<LocationsList />} />
        <Route path="/locations/new" element={<LocationAdd />} />
        <Route path="/locations/import" element={<LocationImport />} />
        <Route path="/locations/archived" element={<LocationArchives />} />
        <Route path="/locations/:storeId" element={<LocationDetail />} />
        {/* §15z sections whose phase has not landed — the finished IA from day one. */}
        <Route path="/promotions" element={<Promotions />} />
        <Route path="/promotions/circulars" element={<Circulars />} />
        <Route path="/promotions/circulars/:issueId" element={<Circulars />} />
        <Route path="/orders" element={<Orders />} />
        <Route path="/orders/:orderLocationId" element={<OrderDetail />} />
        <Route path="/payouts" element={<Payouts />} />
        <Route path="/payouts/adjustments/:adjustmentId" element={<AdjustmentAppeal />} />
        <Route path="/payouts/:payoutId" element={<Payouts />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/recalls" element={<ComingSoon sectionKey="recalls" icon="foodrecalls" />} />
        <Route path="/business" element={<BusinessHome />} />
        <Route path="/business/team" element={<Team />} />
        <Route path="/business/profile" element={<StoreProfile />} />
        <Route path="/business/contacts" element={<SupportContacts />} />
        <Route path="/business/certifications" element={<Certifications />} />
        <Route path="/business/ownership" element={<Ownership />} />
        {/* Old bookmarks remain safe while the approved IA has one Business owner. */}
        <Route path="/profile" element={<Navigate to="/business/profile" replace />} />
        <Route path="/team" element={<Navigate to="/business/team" replace />} />
        <Route path="/help" element={<Help />} />
        <Route path="/help/:articleId" element={<Help />} />
        <Route path="/confirmation" element={<Navigate to="/products?status=needs_answer" replace />} />
        <Route path="*" element={<Home />} />
      </Routes>
    </MainLayout>
  );
}

/**
 * Landing: a vendor still being onboarded (Vendor.status === 'onboarding') sees
 * the §2 Setup/pending status screen; an active vendor sees the dashboard.
 */
function Home() {
  const { loading, status } = useSetup();
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <Spinner color="brand" />
      </Box>
    );
  }
  return status === 'onboarding' ? <SetupPending /> : <Dashboard />;
}

function Loading({ message }: { message: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        fontFamily: 'Montserrat, sans-serif',
        color: '#5b6573',
      }}
    >
      {message}
    </div>
  );
}
